import type { AccountKind, EntityType, SearchHit } from "../api/searchApi";
import { TYPE_LABEL, TYPE_ORDER } from "./actions";

/**
 * Faceted filtering of a loaded result set, the way a store's left rail works:
 * several independent filter groups; picking options within one group widens
 * it (OR), groups combine narrowly (AND); every option shows how many hits it
 * would leave given the OTHER groups' picks, so a count is exactly what a click
 * does. Pure functions — the dialog owns the state.
 */
export type FacetKey = "type" | "status" | "billing" | "accountKind" | "client" | "employer";

export type FacetState = Readonly<Record<FacetKey, readonly string[]>>;

export const FACET_KEYS: readonly FacetKey[] = [
  "type",
  "status",
  "billing",
  "accountKind",
  "client",
  "employer",
];

export const EMPTY_FACETS: FacetState = {
  type: [],
  status: [],
  billing: [],
  accountKind: [],
  client: [],
  employer: [],
};

export const FACET_TITLE: Record<FacetKey, string> = {
  type: "Type",
  status: "Status",
  billing: "Billing",
  accountKind: "Account kind",
  client: "Insurance partner",
  employer: "Employer group",
};

export interface FacetOption {
  value: string;
  label: string;
  count: number;
  selected: boolean;
}

export interface FacetGroup {
  key: FacetKey;
  title: string;
  options: FacetOption[];
}

export interface FacetChip {
  key: FacetKey;
  value: string;
  label: string;
}

const HIERARCHY_TYPES: readonly EntityType[] = [
  "CLIENT",
  "EMPLOYER",
  "GROUP",
  "DIVISION",
  "LOCATION",
];

const STATUS_LABEL: Record<string, string> = { ACTIVE: "Active", INACTIVE: "Inactive" };
const BILLING_LABEL: Record<string, string> = {
  HAS: "Has billing account",
  NONE: "No billing account",
};
const ACCOUNT_KIND_LABEL: Record<AccountKind, string> = {
  CONSOLIDATED: "Consolidated",
  PER_PREFERENCE: "Per location / preference",
  MANUAL: "Manual",
};

/** Groups whose options have a canonical order; the rest sort by count, then name. */
const FIXED_ORDER: Partial<Record<FacetKey, readonly string[]>> = {
  type: TYPE_ORDER,
  status: ["ACTIVE", "INACTIVE"],
  billing: ["HAS", "NONE"],
  accountKind: ["CONSOLIDATED", "PER_PREFERENCE", "MANUAL"],
};

/** The value a hit contributes to a group; null = the group does not apply to this hit. */
export function facetValue(hit: SearchHit, key: FacetKey): string | null {
  switch (key) {
    case "type":
      return hit.type;
    case "status":
      return hit.status ? hit.status.toUpperCase() : null;
    case "billing":
      // Only hierarchy entities are "billed on" an account; the account itself and agents are not.
      return HIERARCHY_TYPES.includes(hit.type) ? (hit.billingAccountRoute ? "HAS" : "NONE") : null;
    case "accountKind":
      return hit.type === "BILLING_ACCOUNT" ? (hit.accountKind ?? null) : null;
    case "client":
      return hit.clientName || null;
    case "employer":
      return hit.employerName || null;
  }
}

export function facetLabel(key: FacetKey, value: string): string {
  switch (key) {
    case "type":
      return TYPE_LABEL[value as EntityType] ?? value;
    case "status":
      return STATUS_LABEL[value] ?? value;
    case "billing":
      return BILLING_LABEL[value] ?? value;
    case "accountKind":
      return ACCOUNT_KIND_LABEL[value as AccountKind] ?? value;
    default:
      return value;
  }
}

/**
 * A hit passes when, for every group with picks (except `skip`), its value is
 * one of them. A hit the group does not apply to is excluded once that group
 * has picks — picking "Active" hides rows that carry no status, as in any store.
 */
export function matchesFacets(hit: SearchHit, facets: FacetState, skip?: FacetKey): boolean {
  for (const key of FACET_KEYS) {
    if (key === skip) continue;
    const picks = facets[key];
    if (picks.length === 0) continue;
    const v = facetValue(hit, key);
    if (v === null || !picks.includes(v)) return false;
  }
  return true;
}

export const applyFacets = (hits: readonly SearchHit[], facets: FacetState): SearchHit[] =>
  hits.filter((h) => matchesFacets(h, facets));

/**
 * The rail's groups with live counts. Each group counts over the hits that pass
 * every OTHER group, so its numbers reflect what picking an option would show;
 * a picked option is kept even at zero so it can be un-picked. Groups with
 * nothing to offer are dropped.
 */
export function facetGroups(hits: readonly SearchHit[], facets: FacetState): FacetGroup[] {
  const groups: FacetGroup[] = [];
  for (const key of FACET_KEYS) {
    const counts = new Map<string, number>();
    for (const hit of hits) {
      if (!matchesFacets(hit, facets, key)) continue;
      const v = facetValue(hit, key);
      if (v !== null) counts.set(v, (counts.get(v) ?? 0) + 1);
    }
    for (const picked of facets[key]) {
      if (!counts.has(picked)) counts.set(picked, 0);
    }
    if (counts.size === 0) continue;

    const fixed = FIXED_ORDER[key];
    const values = [...counts.keys()].sort((a, b) =>
      fixed
        ? fixed.indexOf(a) - fixed.indexOf(b)
        : (counts.get(b) ?? 0) - (counts.get(a) ?? 0) || a.localeCompare(b)
    );
    groups.push({
      key,
      title: FACET_TITLE[key],
      options: values.map((value) => ({
        value,
        label: facetLabel(key, value),
        count: counts.get(value) ?? 0,
        selected: facets[key].includes(value),
      })),
    });
  }
  return groups;
}

export function toggleFacet(facets: FacetState, key: FacetKey, value: string): FacetState {
  const picks = facets[key];
  return {
    ...facets,
    [key]: picks.includes(value) ? picks.filter((v) => v !== value) : [...picks, value],
  };
}

export const activeFacetCount = (facets: FacetState): number =>
  FACET_KEYS.reduce((n, k) => n + facets[k].length, 0);

/** The picked options in group order — the removable chips above the results. */
export const activeFacetChips = (facets: FacetState): FacetChip[] =>
  FACET_KEYS.flatMap((key) =>
    facets[key].map((value) => ({ key, value, label: facetLabel(key, value) }))
  );
